import { useQuery } from '@apollo/client';
import { useCallback, useMemo } from 'react';

import type {
  CustomPacingReport,
  CustomPacingReportListQuery,
  CustomPacingReportListQueryVariables,
  OrderOption,
} from '../../../../../../apis/graphql';
import { removeTypeNamesFromObject } from '../../../../../../apis/graphql/utils';
import { DEFAULT_REPORT_LIST_SORT_BY, REPORT_LIST_LIMIT } from '../../constants';
import { GET_CUSTOM_PACING_REPORT_LIST } from '../../queries';

export type UseReportList = {
  loading: boolean;
  reportList: CustomPacingReport[];
  fetchMore: () => void;
  hasMore: boolean;
  errorMessage?: string;
};

export const useReportList = (sortOrder: OrderOption): UseReportList => {
  const { data, loading, fetchMore, error } = useQuery<
    CustomPacingReportListQuery,
    CustomPacingReportListQueryVariables
  >(GET_CUSTOM_PACING_REPORT_LIST, {
    variables: {
      sortOptions: {
        sortBy: DEFAULT_REPORT_LIST_SORT_BY,
        sortOrder,
      },
      paginationOptions: { limit: REPORT_LIST_LIMIT, offset: 0 },
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const reportList: CustomPacingReport[] = useMemo(() => {
    return data?.getCustomPacingReportList?.edges.map((node) => removeTypeNamesFromObject(node.node)) || [];
  }, [data?.getCustomPacingReportList?.edges]);

  const hasMore = !!data?.getCustomPacingReportList?.pageInfo.hasNextPage;

  const onFetchMore = useCallback(() => {
    if (loading || !hasMore) return;

    fetchMore({
      variables: {
        paginationOptions: { limit: REPORT_LIST_LIMIT, offset: reportList.length },
        sortOptions: {
          sortBy: DEFAULT_REPORT_LIST_SORT_BY,
          sortOrder,
        },
      },
    });
  }, [loading, hasMore, reportList.length, sortOrder, fetchMore]);

  return {
    loading,
    reportList,
    fetchMore: onFetchMore,
    hasMore,
    errorMessage: error?.message,
  };
};
